import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { FileWarning, Loader2, Download, CheckCircle2, AlertTriangle } from 'lucide-react';
import { walletIntelApi } from '@/api/walletIntel';
import { VERDICT_COLOR } from '@/types/walletIntel';
import { useAuth } from '@/contexts/AuthContext';
import { WalletIntelSubNav } from '@/components/wallet-intel/SubNav';
import { useBackgroundJobs } from '@/contexts/BackgroundJobsContext';

const CHAINS = [
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'tron', label: 'Tron' },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="tracking-[0.1em] mb-2" style={{ color: '#7C8CA6', fontSize: 11.5, fontWeight: 700 }}>{label}</p>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: '#050709', border: '1px solid #182030', color: '#DCE8F5', fontSize: 13,
};

export default function SarExport() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [address, setAddress] = useState(searchParams.get('address') ?? '');
  const [chain, setChain] = useState(searchParams.get('chain') ?? 'ethereum');
  const [reportingEntity, setReportingEntity] = useState('');
  const [filedBy, setFiledBy] = useState(user?.full_name || user?.email || '');
  const [caseId, setCaseId] = useState('');
  const [narrative, setNarrative] = useState('');
  const [includeExposure, setIncludeExposure] = useState(true);

  const { runJob } = useBackgroundJobs();

  const mutation = useMutation({
    // PDF rendering can take a while — wrapped in runJob so it's still
    // tracked (and its download link still reachable) if the analyst moves
    // on to another page before it finishes.
    mutationFn: () =>
      runJob(
        `SAR/STR report for ${address.trim() || 'wallet'}`,
        () => walletIntelApi.sarExport({
          chain,
          address: address.trim(),
          reporting_entity_name: reportingEntity.trim(),
          filed_by: filedBy.trim(),
          case_id: caseId.trim() || undefined,
          narrative: narrative.trim() || undefined,
          include_exposure: includeExposure,
        }),
        { href: (result) => result.download_url },
      ),
  });

  const canSubmit = address.trim() && reportingEntity.trim() && filedBy.trim() && !mutation.isPending;

  return (
    <div style={{ background: '#050709', minHeight: '100%' }} className="flex flex-col -m-6 p-6">
      <WalletIntelSubNav />
      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12 }} className="px-6 py-4 mb-6">
        <h1 style={{ color: '#F2F6FC', fontFamily: "'Space Grotesk', monospace", fontWeight: 800, fontSize: 20 }}>SAR / STR Export</h1>
        <p style={{ color: '#8D96AC', fontSize: 13, marginTop: 3 }}>
          FATF/FIU-IND-shaped suspicious transaction report, with source-reliability grading per citation
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: 20 }}
          className="flex flex-col gap-4"
        >
          <Field label="WALLET ADDRESS *">
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="0x..."
              className="w-full rounded-lg px-3 py-2.5 font-mono outline-none" style={inputStyle} />
          </Field>
          <Field label="CHAIN">
            <select value={chain} onChange={(e) => setChain(e.target.value)}
              className="w-full rounded-lg px-3 py-2 font-mono text-[11px] outline-none" style={inputStyle}>
              {CHAINS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="REPORTING ENTITY *">
            <input value={reportingEntity} onChange={(e) => setReportingEntity(e.target.value)} placeholder="e.g. Sycek Compliance Desk"
              className="w-full rounded-lg px-3 py-2.5 font-mono outline-none" style={inputStyle} />
          </Field>
          <Field label="FILED BY *">
            <input value={filedBy} onChange={(e) => setFiledBy(e.target.value)} placeholder="Compliance officer name/role"
              className="w-full rounded-lg px-3 py-2.5 font-mono outline-none" style={inputStyle} />
          </Field>
          <Field label="CASE ID (OPTIONAL)">
            <input value={caseId} onChange={(e) => setCaseId(e.target.value)} placeholder="Attach to an investigation case"
              className="w-full rounded-lg px-3 py-2.5 font-mono outline-none" style={inputStyle} />
          </Field>
          <Field label="ANALYST NARRATIVE (OPTIONAL)">
            <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={3}
              placeholder="Free-text description of the suspicious activity beyond the automated findings"
              className="w-full rounded-lg px-3 py-2 font-mono text-[10.5px] outline-none resize-none" style={inputStyle} />
          </Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeExposure} onChange={(e) => setIncludeExposure(e.target.checked)} />
            <span style={{ color: '#8D96AC', fontSize: 12.5 }}>Include indirect-exposure analysis</span>
          </label>

          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-bold tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: '#FF6B1A18', color: '#FF6B1A', border: '1px solid #FF6B1A44', fontSize: 13 }}
          >
            {mutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileWarning size={14} />}
            GENERATE SAR/STR PDF
          </motion.button>

          {mutation.isError && (
            <div className="flex items-center gap-2" style={{ color: '#FF1F1F' }}>
              <AlertTriangle size={14} />
              <span style={{ fontSize: 12 }}>{(mutation.error as Error)?.message}</span>
            </div>
          )}
        </motion.div>

        {/* Result panel */}
        <div>
          {!mutation.data && !mutation.isPending && (
            <div style={{ background: '#0C1118', border: '1px dashed #182030', borderRadius: 12, padding: '48px 28px', textAlign: 'center', height: '100%' }}
              className="flex flex-col items-center justify-center">
              <FileWarning size={26} style={{ color: '#3A5A7A', marginBottom: 12 }} />
              <p style={{ color: '#8D96AC', fontSize: 13.5 }}>Your generated report will appear here.</p>
            </div>
          )}

          {mutation.isPending && (
            <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 12, padding: '48px 28px', height: '100%' }}
              className="flex flex-col items-center justify-center">
              <Loader2 className="animate-spin mb-3" size={22} style={{ color: '#00C8FF' }} />
              <p style={{ color: '#8D96AC', fontSize: 13.5 }}>Assembling report and rendering PDF…</p>
            </div>
          )}

          {mutation.data && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}
              style={{ background: '#0C1118', border: '1px solid #00C85330', borderRadius: 12, padding: 26 }}
              className="flex flex-col items-center text-center"
            >
              <CheckCircle2 size={32} style={{ color: '#00C853', marginBottom: 12 }} />
              <p style={{ color: '#F2F6FC', fontWeight: 700, fontSize: 15.5 }}>Report generated</p>
              <p style={{ color: '#5A7A9A', fontSize: 11.5, marginTop: 5 }}>ID: {mutation.data.report_id}</p>
              <p style={{ color: VERDICT_COLOR[mutation.data.verdict], fontWeight: 600, fontSize: 13, marginTop: 7 }}>
                Verdict: {mutation.data.verdict.replace(/_/g, ' ').toUpperCase()}
              </p>
              {mutation.data.verdict === 'needs_review' && (
                <p style={{ color: '#F0B429', fontSize: 11, marginTop: 4, maxWidth: 260 }}>
                  A sanctions or exposure check errored or is unavailable — this is not a clean result. See the PDF for details before relying on it.
                </p>
              )}
              <motion.a
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                href={mutation.data.download_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold tracking-wide mt-5"
                style={{ background: '#00C8FF18', color: '#00C8FF', border: '1px solid #00C8FF44', fontSize: 12.5 }}
              >
                <Download size={14} /> DOWNLOAD PDF
              </motion.a>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
